import React from 'react';
import Observer, {
  Props as ObserverProps,
} from '@researchgate/react-intersection-observer';
import classnames from 'classnames';

import { screens } from '../styles/breakpoints';

import parseClassnames from '../helpers/parseClassnames';
import getImgxUrl from './getImgxUrl';

import { CuiScreens } from '../styles/types';
import { LazyResponsiveImageProps, ImgxParams } from './types';
import { DEFAULT_PLACEHOLDER } from './constants';

/**
 * Requeriments:
 * - It must have a better api
 * - It must use picture, source and image html5 tags
 * - it must lazy load the images
 * - it must handle the dpr x1 and x2
 * - it must add a "progressive animation" (TODO)
 * - it must use auto=compress (this will serve the image in webp if the browser supports it)
 *
 *
 * ==== Animation ====
 * For the animation we need to use the onload event.
 * 1. Show the placeholder
 * 2. Detect the image is in the viewport (using intersection observer) and then start to load the real image.
 * 3. Once the real image is loaded (using the onload event) we change its opacity and transform css properties
 * 4. Hide the placeholder and show the real one.
 *
 * ==== Use will-transform ====
 * We are going to set the will-transform css property dynamically.
 * This property is used to help the browser to optimize the relayout/repaint of the elements.
 * 1. One the image has been "pre-loaded",  set the will-transform property to the picture element
 * 2. After 300ms (when the animation ends), remove the will-transform property
 */

const LazyImageResponsive: React.FC<LazyResponsiveImageProps> = ({
  placeholder,
  sources,
  imgxParams,
  observerProps,
  alt,
  className = '',
  imgClassName = '',
  style = {},
  pictureStyle = {},
  backgroundImage,
  dark,
}) => {
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const [isResourceLoaded, setIsResourceLoaded] = React.useState(false);

  const commonImgParams = {
    // auto: 'compress',
    fm: 'webp',
    ...imgxParams,
  };
  const placeholderImage =
    placeholder || DEFAULT_PLACEHOLDER[dark ? 'dark' : 'light'];
  const defaultWebpImage = getImgxUrl(
    sources.default,
    commonImgParams as ImgxParams,
  );
  const defaultImage = getImgxUrl(sources.default, imgxParams);

  const handleIntersection: ObserverProps['onChange'] = event => {
    if (event.isIntersecting) {
      setIsIntersecting(true);
    }
  };

  const getSourceTagBySize = (size: keyof CuiScreens): React.ReactNode => {
    const sourceImage = sources[size];

    if (!sourceImage) {
      return null;
    }

    const image = getImgxUrl(sourceImage, commonImgParams as ImgxParams);

    return (
      <source
        media={`(min-width: ${screens[size]})`}
        srcSet={`${image}&dpr=1 1x, ${image}&dpr=2 2x`}
        type="image/webp"
      />
    );
  };

  const options = {
    onChange: handleIntersection,
    rootMargin: '0px 0px 300px 0px',
    disabled: isResourceLoaded,
    ...observerProps,
  };

  if (process.env.NODE_ENV === 'development') {
    if (!sources.sm || !sources.md || !sources.lg) {
      console.error(
        'LazyImageResponsive: Hey, all keys of the sources props are required! Looks like you are missing one of them.',
      );
    }
  }

  return (
    <Observer {...options}>
      <div
        className={parseClassnames(
          className,
          classnames(`block max-w-full h-auto`, {
            relative: !className.includes('absolute'),
          }),
        )}
      >
        <div
          className="absolute inset-0 z-10"
          style={{
            opacity: isResourceLoaded ? 0 : 1,
            backgroundImage: backgroundImage || `url(${placeholderImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transition: 'opacity 0.3s ease-in-out',
            transitionDelay: '100ms',
          }}
        />
        {/*
          We can't show the image immediately because it will show a broken image while the image resource is loaded
          So, we need to handle it with an onLoad event. Only show the image if the image has already loaded.
        */}
        {isIntersecting && (
          <picture className="absolute inset-0" style={pictureStyle}>
            {getSourceTagBySize('lg')}
            {getSourceTagBySize('md')}
            {getSourceTagBySize('sm')}
            <source
              srcSet={`${defaultWebpImage}&dpr=1 1x, ${defaultWebpImage}&dpr=2 2x`}
              media="(max-width: 767px)"
              type="image/webp"
            />
            <img
              style={{
                opacity: isResourceLoaded ? 1 : 0,
                transition: 'opacity 300ms ease-in-out',
                ...style,
              }}
              className={parseClassnames(
                imgClassName,
                'w-full h-full block relative object-contain',
              )}
              srcSet={`${defaultImage}&dpr=1 1x, ${defaultImage}&dpr=2 2x`}
              src={defaultImage}
              alt={alt}
              // this little one is the trick
              onLoad={() => setIsResourceLoaded(true)}
            />
          </picture>
        )}
      </div>
    </Observer>
  );
};

export default LazyImageResponsive;
